import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { emailBackendApi } from '@/lib/email/edge-functions-api';
import { shouldBlockActivation } from './activationPrecheck';
import { captureError } from '@/lib/sentry';
import { toError } from '@/lib/error-handling';
import { isReconnectError } from '@/lib/email/reconnect-error';
import type { 
  HermesEmailCampaign, 
  CreateHermesCampaignRequest, 
  UpdateHermesCampaignRequest 
} from '@/types/hermes-campaign';
import type { CampaignCreationRequest } from '@/lib/email/edge-functions-api';

const STALE_TIME = 30 * 1000; // 30 seconds

// Get all campaigns for current workspace
export function useHermesCampaigns() {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: ['hermes-campaigns', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error('No workspace selected');
      }

      console.log('Fetching hermes campaigns for workspace:', currentWorkspace.id);
      
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch hermes campaigns:', error);
        throw error;
      }

      console.log('Loaded hermes campaigns:', data?.length || 0, 'campaigns');
      return data as HermesEmailCampaign[];
    },
    enabled: !!currentWorkspace?.id,
    staleTime: STALE_TIME,
  });
}

// Get single campaign by ID
export function useHermesCampaign(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['hermes-campaign', campaignId],
    queryFn: async () => {
      if (!campaignId) {
        throw new Error('No campaign ID provided');
      }

      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (error) {
        console.error('Failed to fetch hermes campaign:', error);
        throw error;
      }

      return data as HermesEmailCampaign;
    },
    enabled: !!campaignId,
    staleTime: STALE_TIME,
  });
}

// Create new campaign using Edge Functions
export function useCreateHermesCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignData: CreateHermesCampaignRequest & { recipients: any[] }) => {
      console.log('Creating hermes campaign via Edge Function:', campaignData);
      
      // Transform the data for the Edge Function API.
      // Always create as a DRAFT (draft_mode). A draft's queue jobs are never
      // claimed or sent — the processor's claim RPC filters to campaign
      // status='active' and validateEmailSend re-checks it — so this is a safe
      // checkpoint. The wizard flips draft → active via useActivateHermesCampaign
      // as a deliberate, separate step.
      const edgeFunctionRequest: CampaignCreationRequest = {
        name: campaignData.name,
        workspace_id: campaignData.workspace_id,
        subject_template: campaignData.subject_template,
        body_template: campaignData.body_template,
        email_account_id: campaignData.email_account_id,
        recipients: campaignData.recipients,
        scheduled_start: campaignData.scheduled_start,
        skip_confirmation: false,
        force_activate: false,
        scheduling_mode: campaignData.scheduling_mode,
        // Applied at job-creation time by the scheduler (clamped >= 15 server-side).
        send_interval_minutes: campaignData.send_interval_minutes,
        draft_mode: true,
        // Forwarded so a retried create (network blip) returns the existing
        // draft instead of creating a duplicate.
        idempotency_key: campaignData.idempotency_key,
      };

      console.log('Edge Function request about to be sent:', edgeFunctionRequest);
      const response = await emailBackendApi.createCampaign(edgeFunctionRequest);
      
      if (!response.success) {
        // Preserve the machine-readable error code from the edge function body
        // (e.g. 'no_opted_in_recipients') so the wizard catch can surface an
        // actionable toast and GlitchTip can group failures by code.
        const err = new Error(response.error || 'Failed to create campaign') as Error & {
          error_code?: string;
          details?: Record<string, unknown>;
        };
        if (response.error_code) err.error_code = response.error_code;
        if (response.details) err.details = response.details;
        throw err;
      }

      console.log('Created campaign via Edge Function:', response.data?.campaign_id);
      
      // Return the campaign data in the expected format
      return {
        id: response.data?.campaign_id || '',
        workspace_id: campaignData.workspace_id,
        user_id: campaignData.user_id,
        email_account_id: campaignData.email_account_id,
        status: response.data?.status || campaignData.status || 'draft',
        activated_at: null,
        activated_by: null,
        recipient_list_hash: null,
        recipient_count: campaignData.recipient_count,
        name: campaignData.name,
        subject_template: campaignData.subject_template,
        body_template: campaignData.body_template,
        scheduled_start: campaignData.scheduled_start || null,
        scheduled_end: campaignData.scheduled_end || null,
        emails_sent: null,
        emails_failed: null,
        emails_pending: null,
        idempotency_key: campaignData.idempotency_key,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as HermesEmailCampaign;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.setQueryData(['hermes-campaign', data.id], data);
      
      toast({
        title: "Campaign Created",
        description: `Campaign "${data.name}" created successfully in hermes_ tables`,
      });
    },
    onError: (error: any) => {
      console.error('Campaign creation error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create campaign",
        variant: "destructive",
      });
    },
  });
}

// Update campaign
export function useUpdateHermesCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      campaignId, 
      updates 
    }: { 
      campaignId: string; 
      updates: UpdateHermesCampaignRequest 
    }) => {
      console.log('Updating hermes campaign:', campaignId, updates);
      
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .update(updates)
        .eq('id', campaignId)
        .select()
        .single();

      if (error) {
        console.error('Failed to update hermes campaign:', error);
        throw error;
      }

      console.log('Updated hermes campaign:', data.id);
      return data as HermesEmailCampaign;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.setQueryData(['hermes-campaign', data.id], data);
      
      toast({
        title: "Campaign Updated",
        description: `Campaign "${data.name}" updated successfully`,
      });
    },
    onError: (error: any) => {
      console.error('Campaign update error:', error);
      captureError(error as Error, { feature: 'hermes-update-campaign' });
      toast({
        title: "Error",
        description: error.message || "Failed to update campaign",
        variant: "destructive",
      });
    },
  });
}

// Delete campaign
export function useDeleteHermesCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      console.log('Deleting hermes campaign:', campaignId);
      
      const { error } = await supabase
        .from('hermes_email_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) {
        console.error('Failed to delete hermes campaign:', error);
        throw error;
      }

      console.log('Deleted hermes campaign:', campaignId);
      return campaignId;
    },
    onSuccess: (campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.removeQueries({ queryKey: ['hermes-campaign', campaignId] });
      
      toast({
        title: "Campaign Deleted",
        description: "Campaign deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Campaign deletion error:', error);
      captureError(error as Error, { feature: 'hermes-delete-campaign' });
      toast({
        title: "Error",
        description: error.message || "Failed to delete campaign",
        variant: "destructive",
      });
    },
  });
}

// Fetch all queue jobs for a campaign (used by summary + preview)
export function useHermesCampaignJobs(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['hermes-campaign-jobs', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hermes_email_queue_jobs')
        .select('id, recipient_email, recipient_data, rendered_subject, rendered_body, scheduled_send_time, status, attempts, error_message, sent_at, campaign_id')
        .eq('campaign_id', campaignId!)
        .order('scheduled_send_time', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!campaignId,
    staleTime: 30_000,
  });
}

// Update a single queue job's rendered content (for per-recipient editing)
export function useUpdateQueueJobContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, rendered_subject, rendered_body }: {
      jobId: string; rendered_subject: string; rendered_body: string;
    }) => {
      const { data, error } = await supabase
        .from('hermes_email_queue_jobs')
        .update({ rendered_subject, rendered_body })
        .eq('id', jobId)
        .eq('status', 'pending')
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaign-jobs', data.campaign_id] });
    },
  });
}

// Shared by useActivateHermesCampaign (wizard) and useStartDraftCampaign (Start on a draft).
async function assertSenderCanRefresh(campaignId: string, userId: string): Promise<void> {
  // S2 layer 1 (UX): refuse to activate if the campaign's sending account
  // can't refresh its token. Gives an immediate, clear block instead of an
  // "Active, 0 sent" campaign. The DB trigger (migration
  // hermes_require_healthy_sender_on_activate) is the un-bypassable backstop
  // and covers the cases this frontend pre-check intentionally skips.
  const { data: campaignRow, error: campErr } = await supabase
    .from('hermes_email_campaigns')
    .select('email_account_id, workspace_id')
    .eq('id', campaignId)
    .single();
  if (campErr || !campaignRow) throw campErr ? toError(campErr) : new Error('Campaign not found');

  const { data: acctRow } = await supabase
    .from('hermes_email_accounts')
    .select('email_address')
    .eq('id', campaignRow.email_account_id)
    .single();

  if (acctRow?.email_address) {
    const { data: providerRow } = await supabase
      .from('email_providers')
      .select('id, user_id')
      .eq('email', acctRow.email_address)
      .eq('workspace_id', campaignRow.workspace_id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Only test-refresh when the sending account is OWNED by the activating
    // user — token-refresh is user-scoped and would 404 (false block) for a
    // teammate's provider. Otherwise skip; the DB trigger still backstops.
    if (providerRow?.id && providerRow.user_id === userId) {
      const refreshResult = await supabase.functions.invoke('token-refresh', {
        body: { provider_id: providerRow.id, force_refresh: true },
      });
      const decision = shouldBlockActivation({
        error: refreshResult.error ? refreshResult.error.message : null,
        data: refreshResult.data ?? null,
      });
      if (decision.block) {
        throw new Error(
          `Reconnect ${acctRow.email_address} before sending — ${decision.reason}`,
        );
      }
    }
  }
}

// Activate campaign (triggers backend processing)
export function useActivateHermesCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('User not authenticated');
      }

      await assertSenderCanRefresh(campaignId, currentUser.data.user.id);

      // Update campaign status from draft → active to trigger backend processing
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .update({
          status: 'active',
          activated_at: new Date().toISOString(),
          activated_by: currentUser.data.user.id
        })
        .eq('id', campaignId)
        .eq('status', 'draft')
        .select()
        .single();

      if (error) {
        // PGRST116 = 0 rows matched status='draft'. On a retry after a lost response the
        // campaign is already activated: re-read it and treat that as success.
        if (error.code === 'PGRST116') {
          const { data: current } = await supabase
            .from('hermes_email_campaigns')
            .select('*')
            .eq('id', campaignId)
            .single();
          if (current && (current.status === 'active' || current.status === 'sending')) {
            return current as HermesEmailCampaign;
          }
        }
        console.error('Failed to activate hermes campaign:', error);
        throw toError(error);
      }

      console.log('Activated hermes campaign:', data.id);
      return data as HermesEmailCampaign;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.setQueryData(['hermes-campaign', data.id], data);
      
      toast({
        title: "Campaign Activated",
        description: `Campaign "${data.name}" is now being processed by the backend`,
      });
    },
    onError: (error: any) => {
      console.error('Campaign activation error:', error);
      // Reconnect errors are user-correctable, not bugs: the wizard's catch shows the
      // actionable reconnect toast. Skip Sentry noise + the generic toast here so the
      // correct toast isn't racing this one (masked only by TOAST_LIMIT=1 otherwise).
      if (isReconnectError(error)) return;
      captureError(error as Error, { feature: 'hermes-activate-campaign' });
      toast({
        title: "Error",
        description: error.message || "Failed to activate campaign",
        variant: "destructive",
      });
    },
  });
}

/** The CAN-SPAM activation trigger's rejection (Postgres check_violation 23514). */
export function isMailingAddressGateError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null;
  return e?.code === '23514' && /physical mailing address/i.test(e.message ?? '');
}

// Start a saved DRAFT (left behind by Save Draft or a failed wizard activation).
// resume_campaign_rescheduled accepts 'draft' as well as 'paused': in one transaction it
// re-paces the pending jobs behind the account's active queue, flips the campaign to
// 'active' (stamping activated_at/by), and row-locks it so a concurrent call fails instead
// of double-activating. The DB activation triggers fire inside it (23514 + readable message).
// No suppression pre-flight here (the wizard has one): the backend drops suppressed
// recipients at send time.
export function useStartDraftCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('User not authenticated');
      }
      await assertSenderCanRefresh(campaignId, currentUser.data.user.id);
      const { data, error } = await supabase.rpc('resume_campaign_rescheduled', {
        p_campaign_id: campaignId,
      });
      if (error) throw toError(error);
      return data;
    },
    onSuccess: (data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['hermes-campaign', campaignId] });
      const row = Array.isArray(data) ? data[0] : data;
      const n = Number(row?.rescheduled_count ?? 0);
      toast({ title: `Campaign started — ${n} email${n === 1 ? '' : 's'} queued` });
    },
    onError: (error: Error) => {
      // User-correctable gates are surfaced by the caller (reconnect toast /
      // mailing-address dialog); skip GlitchTip noise and the generic toast for them.
      if (isReconnectError(error) || isMailingAddressGateError(error)) return;
      captureError(error, { feature: 'hermes-start-draft-campaign' });
      toast({
        title: 'Could not start campaign',
        description: error.message || 'Failed to start campaign',
        variant: 'destructive',
      });
    },
  });
}
